import { useState, useRef, useEffect } from "react";
import { categories } from "@/mocks/homeContent";

export default function CategorySection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <section className="relative py-16 md:py-20 bg-background-50">
      <div className="px-4 md:px-6 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
            Browse Product Categories
          </h2>
          <p className="text-base text-foreground-600 max-w-xl mx-auto">
            Select a category to explore the AllChinaBuySpreadsheet database on our main platform.
          </p>
        </div>

        {/* Mobile Dropdown */}
        <div className="flex justify-center mb-10 lg:hidden" ref={dropdownRef}>
          <div className="relative w-full max-w-md">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full flex items-center justify-between px-5 py-3.5 bg-background-100 border border-background-300/60 rounded-lg text-foreground-800 hover:border-primary-300 transition-colors cursor-pointer"
            >
              <span className="text-sm font-medium">
                {dropdownOpen ? "Select a category..." : "Choose a product category..."}
              </span>
              <i
                className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform duration-200 ${
                  dropdownOpen ? "rotate-180" : ""
                }`}
              ></i>
            </button>
            {dropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 border border-background-200/70 rounded-lg overflow-hidden z-30 max-h-72 overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setDropdownOpen(false)}
                    className="flex items-center gap-3 px-5 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer border-b border-background-100 last:border-b-0"
                  >
                    <img src={cat.image} alt={cat.name} className="w-8 h-8 rounded-md object-cover flex-shrink-0" />
                    <span>{cat.name}</span>
                    <i className="ri-arrow-right-up-line text-xs text-foreground-400 ml-auto"></i>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Desktop Category Grid with Images */}
        <div className="hidden lg:grid grid-cols-5 gap-4">
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 hover:shadow-sm transition-all cursor-pointer"
            >
              <div className="relative w-full h-36 overflow-hidden">
                <img
                  src={cat.image}
                  alt={cat.name}
                  title={`AllChinaBuySpreadsheet ${cat.name} category`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <div className="flex items-center justify-between px-3 py-2.5">
                <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                  {cat.name}
                </span>
                <i className="ri-arrow-right-up-line text-xs text-foreground-400 group-hover:text-primary-500 transition-colors"></i>
              </div>
            </a>
          ))}
        </div>

        {/* Tablet/Mobile Grid - Compact */}
        <div className="lg:hidden grid grid-cols-2 sm:grid-cols-3 gap-3">
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
            >
              <div className="relative w-full h-28 overflow-hidden">
                <img
                  src={cat.image}
                  alt={cat.name}
                  title={`AllChinaBuySpreadsheet ${cat.name} category`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <div className="px-2.5 py-2">
                <span className="text-xs font-medium text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-1">
                  {cat.name}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}